import { DataVersion, UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { Button } from '@hakimo-ui/shared/ui-base';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useState } from 'react';
import {
  SITUATION_OPTIONS,
  type ResponderType,
  type TopSituation,
} from '../util';
import { ArrivalFields } from './ArrivalFields';
import { DispatchFields } from './DispatchFields';
import { EvidenceFields } from './EvidenceFields';
import { badge, LABEL } from './util';

const FIELDS_MAP = {
  evidence: EvidenceFields,
  dispatch: DispatchFields,
  arrival: ArrivalFields,
} as const;

interface SnapshotUploaderProps {
  locationAlarmId: string;
  onUploadSuccess?: (action: TopSituation) => void;
  onClose?: () => void;
  product?: UnifiedMonitoringProduct;
  defaultSituation?: TopSituation;
  defaultResponder?: ResponderType;
  lockSituation?: boolean;
  showTitle?: boolean;
  titleInCard?: boolean;
  bare?: boolean;
  timezone?: string;
  /** Overrides the alarm-group context for callers rendered outside a provider. */
  dataVersion?: DataVersion;
}

export function SnapshotUploader({
  locationAlarmId,
  onUploadSuccess,
  onClose,
  product,
  defaultSituation = 'evidence',
  defaultResponder,
  lockSituation = false,
  showTitle = true,
  titleInCard = false,
  bare = false,
  timezone,
  dataVersion,
}: SnapshotUploaderProps) {
  const [situation, setSituation] = useState<TopSituation>(defaultSituation);
  const [situationTouched, setSituationTouched] = useState(
    defaultSituation !== 'evidence'
  );
  const [resetKey, setResetKey] = useState(0);

  const selected =
    SITUATION_OPTIONS.find((o) => o.value === situation) ??
    SITUATION_OPTIONS[0];

  const titleBlock = showTitle && (
    <div className="flex items-center justify-between">
      <h3 className="text-primary-800 dark:text-primary-400 font-sans font-semibold">
        Add Event
      </h3>
      {onClose && (
        <Button variant="icon" onClick={onClose} className="!p-0">
          <XMarkIcon className="h-5 w-5" />
        </Button>
      )}
    </div>
  );

  const handleUploadSuccess = () => {
    if (!lockSituation) {
      setSituation('evidence');
      setSituationTouched(false);
    }
    setResetKey((k) => k + 1);
    onUploadSuccess?.(situation);
  };

  const fieldsProps = {
    locationAlarmId,
    product,
    timezone,
    onUploadSuccess: handleUploadSuccess,
    dataVersion,
    defaultResponder,
  };
  const FieldsComponent = FIELDS_MAP[situation];

  return (
    <div className="flex flex-col gap-1">
      {!titleInCard && titleBlock}
      <div
        className={clsx(
          'flex w-full flex-col',
          bare
            ? 'gap-3.5'
            : 'dark:bg-ondark-bg-2 bg-onlight-bg-2 border-onlight-bg-3 dark:border-ondark-bg-3 gap-2.5 rounded-xl border px-2.5 py-3'
        )}
      >
        {titleInCard && titleBlock}

        <div className="flex flex-wrap items-center gap-2.5">
          {badge(1, situationTouched)}
          <span className={LABEL}>Action</span>
          {lockSituation && (
            <span className="dark:bg-ondark-bg-3 border-onlight-bg-3 dark:border-ondark-line-3 flex min-w-32 items-center gap-2.5 rounded border bg-white px-2.5 py-1 text-sm">
              <span className={`h-2 w-2 rounded-full ${selected.dotClass}`} />
              <span className="dark:text-ondark-text-1 text-sm">
                {selected.label}
              </span>
            </span>
          )}
          {!lockSituation && (
            <div className="w-36 text-sm">
              <SelectMenu
                value={SITUATION_OPTIONS.find((o) => o.value === situation)}
                items={[...SITUATION_OPTIONS]}
                displayValue={(item) => item?.label ?? ''}
                hideCheckIcon
                renderItem={(item) =>
                  item && (
                    <span className="flex items-center gap-2 text-sm">
                      <span
                        className={`h-2 w-2 rounded-full ${item.dotClass}`}
                      />
                      <span>{item.label}</span>
                    </span>
                  )
                }
                id={(item) => item.value}
                onChange={(opt) => {
                  setSituation(opt.value as TopSituation);
                  setSituationTouched(true);
                }}
              />
            </div>
          )}
        </div>

        <FieldsComponent key={`${situation}-${resetKey}`} {...fieldsProps} />
      </div>
    </div>
  );
}
